/**
 * F-027 — Single-touch wrapper.
 *
 * Most browsers already deliver pointerdown/move/up for touch via the
 * unified PointerEvent API the slingshot uses. This module's job is to
 * ignore secondary touches (pinch, two-finger drag) so a stray finger
 * doesn't cancel an aim mid-flight, and to call preventDefault on the
 * primary touch so the page doesn't scroll while the player is aiming.
 *
 * Pure side effect — no state of its own; pairs with createSlingshot.
 */

export interface TouchGuardian {
  detach(): void;
}

export interface TouchGuardOptions {
  /** Optionally restrict to a specific touchType. Default 'touch'. */
  touchType?: PointerEvent["pointerType"];
}

export function attachTouchGuard(
  target: HTMLElement,
  options: TouchGuardOptions = {},
): TouchGuardian {
  const expected = options.touchType ?? "touch";
  let primaryPointerId: number | null = null;

  const onDown = (e: PointerEvent): void => {
    if (e.pointerType !== expected) return;
    if (primaryPointerId === null) {
      primaryPointerId = e.pointerId;
      e.preventDefault?.();
    } else if (e.pointerId !== primaryPointerId) {
      // Secondary touch — silently swallow to avoid pinch interfering.
      e.stopPropagation?.();
      e.preventDefault?.();
    }
  };

  const onMove = (e: PointerEvent): void => {
    if (e.pointerType !== expected) return;
    if (e.pointerId === primaryPointerId) {
      // iOS Safari promotes touchmove to scroll/pull-to-refresh after a few
      // pixels of movement and fires pointercancel on the canvas, killing the
      // slingshot mid-aim. Suppress the default to keep the gesture ours.
      e.preventDefault?.();
    } else {
      e.stopPropagation?.();
      e.preventDefault?.();
    }
  };

  const onEnd = (e: PointerEvent): void => {
    if (e.pointerType !== expected) return;
    if (e.pointerId === primaryPointerId) {
      primaryPointerId = null;
    } else {
      e.stopPropagation?.();
      e.preventDefault?.();
    }
  };

  target.addEventListener("pointerdown", onDown, { passive: false });
  target.addEventListener("pointermove", onMove, { passive: false });
  target.addEventListener("pointerup", onEnd, { passive: false });
  target.addEventListener("pointercancel", onEnd, { passive: false });

  return {
    detach: () => {
      target.removeEventListener("pointerdown", onDown);
      target.removeEventListener("pointermove", onMove);
      target.removeEventListener("pointerup", onEnd);
      target.removeEventListener("pointercancel", onEnd);
      primaryPointerId = null;
    },
  };
}
